import React, { useEffect, useState } from 'react';
import { Check, X, ChevronDown, ChevronUp, Shield, Clock, Star, ShoppingBag } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// --- CONSTANTS & DATA ---
const CHECKOUT_URL = 'https://checkout.ecompradigital.online/cart/45763918561468:1';
const IMAGES = {
  main: 'https://cdn.magicpatterns.com/uploads/6Ss2RL6Rbat8zwbPEoxyKQ/MOCKUP_PRINCIPAL.png',
  bono1: 'https://cdn.magicpatterns.com/uploads/6VWxYs3guZFy3W1ketoNNQ/MOCKUP_BONO-1.png',
  bono2: 'https://cdn.magicpatterns.com/uploads/kB3JKCWLWf2LCEfEV2gwD3/MOCKUP_BONO-2.png',
  bono3: 'https://cdn.magicpatterns.com/uploads/jZG8QcCK7MzYFMMdw9f4eB/MOCKUP_BONO-3.png'
};
const TOMOS = [{
  title: 'Tomo 1 – De Esclavo del Delivery a Dueño del Sistema',
  desc: 'Cambiá la forma de pensar tu negocio. Dejar de sobrevivir y empezar a decidir.'
}, {
  title: 'Tomo 2 – Menú que Vende',
  desc: 'Qué platos sacar, cuáles dejar y cómo armar un menú que cierre pedidos.'
}, {
  title: 'Tomo 3 – Pedidos Todos los Días sin Apps',
  desc: 'Cómo activar pedidos directos y llevarlos a tu WhatsApp.'
}, {
  title: 'Tomo 4 – Vender Sabiendo que Cierra',
  desc: 'Precios, costos y rentabilidad real. Cada pedido tiene que dejar plata.'
}, {
  title: 'Tomo 5 – Clientes que Vuelven Solos',
  desc: 'Cómo lograr que te compren 2, 3 o 4 veces al mes.'
}, {
  title: 'Tomo 6 – Automatizar y Escalar',
  desc: 'Ordenar, delegar y vender sin estar encima todo el día.'
}, {
  title: 'Tomo 7 – Redes y Anuncios que Venden',
  desc: 'De cero a avanzado en Instagram, Facebook y Meta Ads para traer pedidos reales.'
}];
const TESTIMONIALS = [{
  name: 'Juan M.',
  age: '38 años',
  city: 'Buenos Aires',
  quote: 'Pasé de depender de apps a tener pedidos directos todos los días.'
}, {
  name: 'Carla R.',
  age: '34 años',
  city: 'Rosario',
  quote: 'Por primera vez sé cuánto gano por pedido.'
}, {
  name: 'Lucas P.',
  age: '42 años',
  city: 'Córdoba',
  quote: 'Trabajo menos horas y vendo mejor.'
}, {
  name: 'María S.',
  age: '36 años',
  city: 'La Plata',
  quote: 'El sistema es simple y se aplica de verdad.'
}, {
  name: 'Diego F.',
  age: '45 años',
  city: 'Mendoza',
  quote: 'No es teoría. Es lo que necesitaba para ordenar mi negocio.'
}];
const FAQS = [{
  q: '¿Necesito saber marketing o redes?',
  a: 'No. Está explicado paso a paso desde cero.'
}, {
  q: '¿Sirve si ya vendo por apps?',
  a: 'Sí. El sistema te ayuda a depender menos de ellas.'
}, {
  q: '¿Es material digital?',
  a: 'Sí. Acceso inmediato.'
}, {
  q: '¿Cuánto tiempo me lleva aplicarlo?',
  a: 'Desde el primer día ya podés aplicar acciones simples.'
}];
const RECENT_SALES = [{
  name: 'Martín',
  city: 'Buenos Aires'
}, {
  name: 'Sofía',
  city: 'Córdoba'
}, {
  name: 'Fernando',
  city: 'Rosario'
}, {
  name: 'Lucía',
  city: 'Mendoza'
}, {
  name: 'Pablo',
  city: 'La Plata'
}, {
  name: 'Valentina',
  city: 'Mar del Plata'
}, {
  name: 'Javier',
  city: 'Tucumán'
}, {
  name: 'Camila',
  city: 'Salta'
}];
// --- COMPONENTS ---
// Función de scroll suave personalizada (más lenta y elegante)
const smoothScrollTo = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (!element) return;
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset - 50;
  const startPosition = window.pageYOffset;
  const distance = targetPosition - startPosition;
  const duration = 1500; // 1.5 segundos - más lento y suave
  let start: number | null = null;
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };
  const animation = (currentTime: number) => {
    if (start === null) start = currentTime;
    const timeElapsed = currentTime - start;
    const progress = Math.min(timeElapsed / duration, 1);
    const ease = easeInOutCubic(progress);
    window.scrollTo(0, startPosition + distance * ease);
    if (timeElapsed < duration) {
      requestAnimationFrame(animation);
    }
  };
  requestAnimationFrame(animation);
};
const CountdownTimer = () => {
  const [time, setTime] = useState(14 * 60 + 50); // 14:50 in seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setTime(prev => prev > 0 ? prev - 1 : 14 * 60 + 50);
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  return <span className="font-mono font-bold text-yellow-300 text-lg tracking-wider" data-id="element-0">
      {formatTime(time)}
    </span>;
};
// Scroll CTA Button - scrolls to price section
const ScrollCtaButton = ({
  children,
  className = '',
  subtext = '',
  fullWidth = false
}: {
  children: React.ReactNode;
  className?: string;
  subtext?: string;
  fullWidth?: boolean;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    smoothScrollTo('compra');
  };
  return <button onClick={handleClick} className={`
        group relative inline-flex flex-col items-center justify-center 
        bg-gradient-to-b from-green-500 to-green-600 
        hover:from-green-400 hover:to-green-500
        text-white font-black uppercase tracking-wide
        border-b-4 border-green-800 active:border-b-0 active:translate-y-1
        rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200
        px-6 py-4 md:px-10 md:py-5
        cursor-pointer
        ${fullWidth ? 'w-full' : 'w-auto'}
        ${className}
      `} data-id="element-1">
      <span className="flex items-center gap-2 text-lg md:text-2xl lg:text-3xl text-center leading-tight" data-id="element-2">
        {children}
      </span>
      {subtext && <span className="text-xs md:text-sm font-medium opacity-80 mt-1 normal-case" data-id="element-3">
          {subtext}
        </span>}
    </button>;
};
// Direct checkout CTA Button
const CtaButton = ({
  children,
  className = '',
  subtext = '',
  fullWidth = false
}: {
  children: React.ReactNode;
  className?: string;
  subtext?: string;
  fullWidth?: boolean;
}) => <a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" className={`
      group relative inline-flex flex-col items-center justify-center 
      bg-gradient-to-b from-green-500 to-green-600 
      hover:from-green-400 hover:to-green-500
      text-white font-black uppercase tracking-wide
      border-b-4 border-green-800 active:border-b-0 active:translate-y-1
      rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200
      px-6 py-4 md:px-10 md:py-5
      ${fullWidth ? 'w-full' : 'w-auto'}
      ${className}
    `} data-id="element-4">
    <span className="flex items-center gap-2 text-lg md:text-2xl lg:text-3xl text-center leading-tight" data-id="element-5">
      {children}
    </span>
    {subtext && <span className="text-xs md:text-sm font-medium opacity-80 mt-1 normal-case" data-id="element-6">
        {subtext}
      </span>}
  </a>;
const SocialProofPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSale, setCurrentSale] = useState(RECENT_SALES[0]);
  useEffect(() => {
    const showTimer = setInterval(() => {
      const randomSale = RECENT_SALES[Math.floor(Math.random() * RECENT_SALES.length)];
      setCurrentSale(randomSale);
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 4000); // Show for 4 seconds
    }, 12000); // Every 12 seconds
    return () => clearInterval(showTimer);
  }, []);
  return <AnimatePresence data-id="element-7">
      {isVisible && <motion.div initial={{
      x: -300,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: -300,
      opacity: 0
    }} className="fixed bottom-4 left-4 z-50 bg-white rounded-lg shadow-2xl p-4 max-w-xs border-l-4 border-green-500 flex items-center gap-3" data-id="element-8">
          <div className="bg-green-100 p-2 rounded-full" data-id="element-9">
            <ShoppingBag className="w-6 h-6 text-green-600" data-id="element-10" />
          </div>
          <div data-id="element-11">
            <p className="text-sm font-bold text-gray-800" data-id="element-12">
              {currentSale.name} de {currentSale.city}
            </p>
            <p className="text-xs text-gray-600" data-id="element-13">
              acaba de comprar{' '}
              <span className="font-bold text-blue-600" data-id="element-14">KIT DELIVERY PRO</span>
            </p>
            <p className="text-[10px] text-gray-400 mt-1" data-id="element-15">
              Hace unos instantes
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>;
};
const AccordionItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-16">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-4 px-6 flex items-center justify-between text-left hover:bg-gray-50 transition-colors" data-id="element-17">
        <span className="font-bold text-gray-800 text-lg" data-id="element-18">{question}</span>
        {isOpen ? <ChevronUp className="text-blue-600" data-id="element-19" /> : <ChevronDown className="text-gray-400" data-id="element-20" />}
      </button>
      <AnimatePresence data-id="element-21">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-22">
            <div className="p-6 pt-0 text-gray-600 leading-relaxed" data-id="element-23">
              {answer}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
// --- MAIN APP COMPONENT ---
export function App() {
  return <div className="min-h-screen bg-white font-sans text-gray-900 selection:bg-yellow-200" data-id="element-24">
      {/* 1. URGENCY HEADER BAR */}
      <div className="fixed top-0 left-0 w-full bg-red-600 text-white z-50 py-2 px-4 shadow-md" data-id="element-25">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 md:gap-4 text-xs md:text-base font-bold text-center" data-id="element-26">
          <div className="flex items-center gap-2" data-id="element-27">
            <Clock className="w-4 h-4 md:w-5 md:h-5 animate-pulse" data-id="element-28" />
            <span data-id="element-29">OFERTA EXPIRA EN:</span>
            <CountdownTimer data-id="element-30" />
          </div>
          <span className="hidden md:inline" data-id="element-31">|</span>
          <div className="flex items-center gap-1" data-id="element-32">
            <span data-id="element-33">🔥 SOLO QUEDAN 3 CUPOS</span>
          </div>
        </div>
      </div>

      {/* Spacer for fixed header */}
      <div className="h-10 md:h-12" data-id="element-34"></div>

      {/* 2. HERO SECTION */}
      <section className="relative pt-6 pb-10 md:pt-10 md:pb-14 overflow-hidden bg-gray-900" data-id="element-35">
        <div className="max-w-6xl mx-auto px-4 text-center" data-id="element-36">
          <div className="inline-block bg-blue-900/50 text-blue-300 px-3 py-1 rounded-full font-bold text-xs md:text-sm mb-4 border border-blue-700 shadow-sm" data-id="element-37">
            🍔 Para dueños de negocios gastronómicos y delivery
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-4 tracking-tight leading-tight" data-id="element-38">
            KIT DELIVERY{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500" data-id="element-39">
              PRO
            </span>
          </h1>

          <p className="text-base md:text-lg text-gray-300 mb-3 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-40">
            Para el dueño gastronómico que trabaja sin parar, abre cada día sin
            saber si va a vender y siente que todo el esfuerzo nunca alcanza.
          </p>

          <p className="text-lg md:text-xl text-white font-semibold mb-6 max-w-3xl mx-auto leading-relaxed px-2" data-id="element-41">
            Un sistema real para dejar de sobrevivir, generar pedidos todos los
            días y recuperar el control de tu negocio.
          </p>

          <div className="max-w-4xl mx-auto mb-6" data-id="element-42">
            <button onClick={() => smoothScrollTo('compra')} className="block hover:scale-105 transition-transform duration-300 cursor-pointer w-full" data-id="element-43">
              <img src={IMAGES.main} alt="Kit Delivery Pro Pack" className="w-full max-w-2xl mx-auto drop-shadow-2xl" data-id="element-44" />
            </button>
          </div>

          <div className="flex flex-col items-center gap-3" data-id="element-45">
            <ScrollCtaButton className="animate-bounce-slow" data-id="element-46">
              👉 SÍ, QUIERO ACCEDER AHORA
            </ScrollCtaButton>

            <div className="flex items-center gap-2 text-green-400 font-bold text-xs md:text-sm bg-green-900/30 px-3 py-1.5 rounded-full border border-green-700" data-id="element-47">
              <Shield className="w-4 h-4" data-id="element-48" />
              <span data-id="element-49">Garantía de satisfacción de 7 días</span>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PAIN POINT SECTION */}
      <section className="py-10 md:py-14 bg-white" data-id="element-50">
        <div className="max-w-4xl mx-auto px-4" data-id="element-51">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 text-gray-900 px-2" data-id="element-52">
            Si tenés un negocio gastronómico, <br className="hidden sm:block" data-id="element-53" />
            <span className="text-red-600" data-id="element-54">
              dejame hacerte una pregunta honesta
            </span>
          </h3>

          <div className="bg-gray-50 p-5 md:p-8 rounded-2xl border border-gray-100 shadow-lg" data-id="element-55">
            <p className="text-lg md:text-xl font-semibold text-gray-800 mb-6 text-center italic" data-id="element-56">
              "¿Te pasa que abrís el local sin saber cuántos pedidos van a
              entrar hoy?"
            </p>

            <div className="space-y-4 text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-57">
              <p data-id="element-58">No porque tu comida sea mala. No porque no trabajes duro.</p>
              <p data-id="element-59">
                Sino porque nadie te enseñó cómo vender comida de forma
                constante y rentable.
              </p>
              <p data-id="element-60">
                Abrís. Esperás. El teléfono no suena. O suena… pero la plata no
                alcanza.
              </p>
              <p className="font-bold text-gray-900 border-l-4 border-red-500 pl-4" data-id="element-61">
                Y al final del día pensás: "Trabajo como un loco… y sigo igual."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. PROBLEM SECTION */}
      <section className="py-10 md:py-14 bg-gray-900 text-white" data-id="element-62">
        <div className="max-w-4xl mx-auto px-4" data-id="element-63">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6" data-id="element-64">
            Tener un delivery no debería sentirse así
          </h3>

          <p className="text-base md:text-lg text-gray-300 text-center mb-8 max-w-2xl mx-auto" data-id="element-65">
            El problema que nadie dice en voz alta es este: <br data-id="element-66" />
            <span className="text-yellow-400 font-bold" data-id="element-67">
              👉 La mayoría de los negocios gastronómicos no tiene un sistema de
              ventas.
            </span>
          </p>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-center" data-id="element-68">
            <div className="space-y-4 text-base md:text-lg" data-id="element-69">
              <p data-id="element-70">Tiene esfuerzo. Tiene sacrificio. Tiene horas infinitas.</p>
              <p className="text-xl md:text-2xl font-bold text-red-400" data-id="element-71">
                Pero no tiene sistema.
              </p>
              <p data-id="element-72">Si tenés un delivery, esto seguramente te resulta familiar:</p>
            </div>

            <ul className="space-y-3 bg-gray-800 p-5 md:p-6 rounded-xl border border-gray-700 shadow-2xl" data-id="element-73">
              {['Dependés de apps que se llevan una gran parte de tu ganancia', 'No sabés exactamente cuánto ganás por pedido', 'Algunos días vendés bien y otros no entra nadie', 'Trabajás muchas horas y sos el último en cobrar', 'Vivís apagando incendios en vez de hacer crecer el negocio'].map((item, i) => <li key={i} className="flex items-start gap-3 text-sm md:text-base" data-id="element-74">
                  <div className="mt-1.5 min-w-[8px] min-h-[8px] rounded-full bg-red-500 flex-shrink-0" data-id="element-75"></div>
                  <span data-id="element-76">{item}</span>
                </li>)}
            </ul>
          </div>

          <p className="text-center text-base md:text-lg mt-8 font-medium text-gray-300" data-id="element-77">
            Lo peor no es solo la plata.{' '}
            <span className="text-white font-bold border-b-2 border-red-500" data-id="element-78">
              Es la incertidumbre constante.
            </span>
          </p>
        </div>
      </section>

      {/* 5. SOLUTION SECTION */}
      <section className="py-10 md:py-14 bg-white" data-id="element-79">
        <div className="max-w-5xl mx-auto px-4" data-id="element-80">
          <div className="text-center mb-10" data-id="element-81">
            <span className="text-green-600 font-black tracking-widest uppercase text-xs md:text-sm mb-2 block" data-id="element-82">
              ✅ LA SOLUCIÓN
            </span>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-4" data-id="element-83">
              KIT DELIVERY PRO
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-2" data-id="element-84">
              Un sistema completo, práctico y paso a paso, diseñado para que
              cualquier dueño gastronómico pueda:
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-10" data-id="element-85">
            <div className="bg-blue-50 p-5 md:p-6 rounded-2xl border border-blue-100" data-id="element-86">
              <ul className="space-y-3" data-id="element-87">
                {['Generar pedidos todos los días', 'Vender directo por WhatsApp', 'Saber cuánto gana por pedido', 'Lograr que los clientes vuelvan', 'Ordenar y automatizar su negocio'].map((item, i) => <li key={i} className="flex items-center gap-3 text-base md:text-lg font-medium text-gray-800" data-id="element-88">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-89" />
                    <span data-id="element-90">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="flex flex-col justify-center items-center text-center p-5 md:p-6 bg-gray-50 rounded-2xl border border-gray-100" data-id="element-91">
              <p className="text-base md:text-lg text-gray-600 mb-4" data-id="element-92">
                Sin ser experto en marketing. <br data-id="element-93" />
                Sin depender de apps. <br data-id="element-94" />
                Sin improvisar.
              </p>
              <div className="w-full h-px bg-gray-200 mb-4" data-id="element-95"></div>
              <div className="space-y-3 text-left w-full max-w-xs mx-auto" data-id="element-96">
                <div className="flex items-center gap-3" data-id="element-97">
                  <span className="text-xl" data-id="element-98">📂</span>
                  <span className="font-bold text-gray-800 text-sm md:text-base" data-id="element-99">
                    Abrís el material
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-100">
                  <span className="text-xl" data-id="element-101">👆</span>
                  <span className="font-bold text-gray-800 text-sm md:text-base" data-id="element-102">
                    Aplicás el paso del día
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-103">
                  <span className="text-xl" data-id="element-104">📲</span>
                  <span className="font-bold text-gray-800 text-sm md:text-base" data-id="element-105">
                    Empezás a ver pedidos reales
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center" data-id="element-106">
            <ScrollCtaButton data-id="element-107">👉 SÍ, QUIERO ACCEDER AHORA</ScrollCtaButton>
          </div>
        </div>
      </section>

      {/* 6. TOMOS BREAKDOWN */}
      <section className="py-10 md:py-14 bg-gray-50" data-id="element-108">
        <div className="max-w-5xl mx-auto px-4" data-id="element-109">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-center mb-8 md:mb-10 text-gray-900 px-2" data-id="element-110">
            📘 ¿Qué incluye exactamente <br className="hidden sm:block" data-id="element-111" />
            <span className="text-blue-600" data-id="element-112">KIT DELIVERY PRO?</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-4" data-id="element-113">
            {TOMOS.map((tomo, i) => <div key={i} className="bg-white p-4 md:p-5 rounded-xl shadow-sm border border-gray-100" data-id="element-114">
                <h4 className="font-bold text-base md:text-lg text-blue-900 mb-1 flex items-start gap-2" data-id="element-115">
                  <span className="text-blue-500 flex-shrink-0" data-id="element-116">🔹</span>
                  <span data-id="element-117">{tomo.title}</span>
                </h4>
                <p className="text-gray-600 text-sm md:text-base ml-6" data-id="element-118">
                  {tomo.desc}
                </p>
              </div>)}
          </div>

          <div className="mt-8 text-center bg-blue-900 text-white p-5 md:p-6 rounded-xl shadow-lg" data-id="element-119">
            <p className="text-base md:text-lg font-medium" data-id="element-120">
              Todo organizado para que sepas qué hacer primero, qué aplicar
              después y cómo conectar todo en un solo sistema.
            </p>
          </div>
        </div>
      </section>

      {/* 7. BEFORE VS AFTER */}
      <section className="py-10 md:py-14 bg-white" data-id="element-121">
        <div className="max-w-6xl mx-auto px-4" data-id="element-122">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8" data-id="element-123">
            🔄 Antes vs Después
          </h2>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-id="element-124">
            {/* Before */}
            <div className="bg-red-50 p-5 md:p-6 rounded-2xl border border-red-100" data-id="element-125">
              <h3 className="text-xl md:text-2xl font-bold text-red-600 mb-4 flex items-center gap-2" data-id="element-126">
                <X className="w-6 h-6 flex-shrink-0" data-id="element-127" />
                <span data-id="element-128">Antes de KIT DELIVERY PRO</span>
              </h3>
              <ul className="space-y-3" data-id="element-129">
                {['Pedidos irregulares', 'Dependencia total de apps', 'Mucho trabajo, poca ganancia', 'Estrés constante', 'Falta de control'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-700 text-sm md:text-base" data-id="element-130">
                    <div className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" data-id="element-131"></div>
                    <span data-id="element-132">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* After */}
            <div className="bg-green-50 p-5 md:p-6 rounded-2xl border border-green-100 shadow-lg" data-id="element-133">
              <h3 className="text-xl md:text-2xl font-bold text-green-600 mb-4 flex items-center gap-2" data-id="element-134">
                <Check className="w-6 h-6 flex-shrink-0" data-id="element-135" />
                <span data-id="element-136">Después de KIT DELIVERY PRO</span>
              </h3>
              <ul className="space-y-3" data-id="element-137">
                {['Pedidos directos todos los días', 'Más ganancia por venta', 'Control real del negocio', 'Clientes que vuelven', 'Tranquilidad y previsibilidad'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-800 font-medium text-sm md:text-base" data-id="element-138">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-139" />
                    <span data-id="element-140">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 8. BONUSES SECTION */}
      <section className="py-10 md:py-14 bg-gray-900 text-white relative overflow-hidden" data-id="element-141">
        {/* Background pattern effect */}
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:16px_16px]" data-id="element-142"></div>

        <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-143">
          <div className="text-center mb-8 md:mb-10" data-id="element-144">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-yellow-400 mb-2 drop-shadow-lg" data-id="element-145">
              🎁 BONOS ESPECIALES
            </h2>
            <p className="text-xl md:text-2xl font-bold text-white" data-id="element-146">
              GRATIS SOLO HOY
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-id="element-147">
            {/* Bonus 1 */}
            <button onClick={() => smoothScrollTo('compra')} className="group bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all hover:transform hover:scale-105 duration-300 text-left cursor-pointer" data-id="element-148">
              <div className="aspect-square bg-gray-700 relative overflow-hidden" data-id="element-149">
                <img src={IMAGES.bono1} alt="Plantillas Delivery Pro" className="w-full h-full object-cover" data-id="element-150" />
                <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-2 py-1" data-id="element-151">
                  GRATIS
                </div>
              </div>
              <div className="p-4" data-id="element-152">
                <h4 className="text-base md:text-lg font-bold text-yellow-400 mb-1" data-id="element-153">
                  Bono #1 – Plantillas Delivery Pro
                </h4>
                <p className="text-gray-300 text-xs md:text-sm mb-2" data-id="element-154">
                  Mensajes listos para vender, cerrar y repetir pedidos.
                </p>
                <p className="text-xs md:text-sm font-bold" data-id="element-155">
                  <span className="text-gray-500 line-through mr-2" data-id="element-156">
                    Valor: ARS 24.999
                  </span>
                  <span className="text-green-400" data-id="element-157">HOY GRATIS</span>
                </p>
              </div>
            </button>

            {/* Bonus 2 */}
            <button onClick={() => smoothScrollTo('compra')} className="group bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all hover:transform hover:scale-105 duration-300 text-left cursor-pointer" data-id="element-158">
              <div className="aspect-square bg-gray-700 relative overflow-hidden" data-id="element-159">
                <img src={IMAGES.bono2} alt="Plan de Activación 7 Días" className="w-full h-full object-cover" data-id="element-160" />
                <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-2 py-1" data-id="element-161">
                  GRATIS
                </div>
              </div>
              <div className="p-4" data-id="element-162">
                <h4 className="text-base md:text-lg font-bold text-yellow-400 mb-1" data-id="element-163">
                  Bono #2 – Plan de Activación 7 Días
                </h4>
                <p className="text-gray-300 text-xs md:text-sm mb-2" data-id="element-164">
                  Qué hacer cada día para arrancar sin perderte.
                </p>
                <p className="text-xs md:text-sm font-bold" data-id="element-165">
                  <span className="text-gray-500 line-through mr-2" data-id="element-166">
                    Valor: ARS 24.999
                  </span>
                  <span className="text-green-400" data-id="element-167">HOY GRATIS</span>
                </p>
              </div>
            </button>

            {/* Bonus 3 */}
            <button onClick={() => smoothScrollTo('compra')} className="group bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-yellow-400 transition-all hover:transform hover:scale-105 duration-300 sm:col-span-2 md:col-span-1 text-left cursor-pointer" data-id="element-168">
              <div className="aspect-square bg-gray-700 relative overflow-hidden" data-id="element-169">
                <img src={IMAGES.bono3} alt="Calendario de Contenido Delivery" className="w-full h-full object-cover" data-id="element-170" />
                <div className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold px-2 py-1" data-id="element-171">
                  GRATIS
                </div>
              </div>
              <div className="p-4" data-id="element-172">
                <h4 className="text-base md:text-lg font-bold text-yellow-400 mb-1" data-id="element-173">
                  Bono #3 – Calendario de Contenido Delivery
                </h4>
                <p className="text-gray-300 text-xs md:text-sm mb-2" data-id="element-174">
                  Qué publicar durante 90 días sin pensar.
                </p>
                <p className="text-xs md:text-sm font-bold" data-id="element-175">
                  <span className="text-gray-500 line-through mr-2" data-id="element-176">
                    Valor: ARS 19.999
                  </span>
                  <span className="text-green-400" data-id="element-177">HOY GRATIS</span>
                </p>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* 9. SUMMARY & 10. PRICE SECTION */}
      <section id="compra" className="py-10 md:py-14 bg-white" data-id="element-178">
        <div className="max-w-4xl mx-auto px-4" data-id="element-179">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border-2 border-gray-100 overflow-hidden" data-id="element-180">
            <div className="bg-gray-50 p-5 md:p-6 border-b border-gray-100" data-id="element-181">
              <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6" data-id="element-182">
                📦 Lo que recibís hoy
              </h3>
              <ul className="space-y-3 max-w-xl mx-auto" data-id="element-183">
                <li className="flex items-start gap-3 text-base md:text-lg font-bold text-gray-800" data-id="element-184">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-185" />
                  <span data-id="element-186">7 Tomos completos de KIT DELIVERY PRO</span>
                </li>
                <li className="flex items-start gap-3 text-sm md:text-base text-gray-700" data-id="element-187">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-188" />
                  <span data-id="element-189">
                    <strong data-id="element-190">Bono #1:</strong> Plantillas Delivery Pro
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm md:text-base text-gray-700" data-id="element-191">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-192" />
                  <span data-id="element-193">
                    <strong data-id="element-194">Bono #2:</strong> Plan de Activación 7 Días
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm md:text-base text-gray-700" data-id="element-195">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-196" />
                  <span data-id="element-197">
                    <strong data-id="element-198">Bono #3:</strong> Calendario de Contenido Delivery
                  </span>
                </li>
                <li className="flex items-start gap-3 text-sm md:text-base text-gray-700" data-id="element-199">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-200" />
                  <span data-id="element-201">Acceso inmediato</span>
                </li>
                <li className="flex items-start gap-3 text-sm md:text-base text-gray-700" data-id="element-202">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-203" />
                  <span data-id="element-204">Descarga digital</span>
                </li>
                <li className="flex items-start gap-3 text-sm md:text-base text-gray-700" data-id="element-205">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-206" />
                  <span data-id="element-207">Acceso de por vida</span>
                </li>
              </ul>
            </div>

            <div className="p-5 md:p-8 text-center bg-blue-50/50" data-id="element-208">
              <p className="text-gray-500 text-base md:text-lg mb-2" data-id="element-209">
                Precio regular:{' '}
                <span className="line-through decoration-red-500" data-id="element-210">
                  ARS $49.999
                </span>
              </p>
              <p className="text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 tracking-tight" data-id="element-211">
                🔥 HOY SOLO: <span className="text-green-600" data-id="element-212">ARS $19.990</span>
              </p>

              <CtaButton fullWidth className="max-w-2xl mx-auto" data-id="element-213">
                👉 OBTENER KIT DELIVERY PRO AHORA
              </CtaButton>

              <div className="mt-4 flex items-center justify-center gap-2 text-gray-500 text-xs md:text-sm" data-id="element-214">
                <Shield className="w-4 h-4" data-id="element-215" />
                <span data-id="element-216">Pago 100% Seguro y Encriptado</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. GUARANTEE */}
      <section className="py-10 md:py-12 bg-white border-t border-gray-100" data-id="element-217">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-218">
          <div className="inline-block bg-yellow-100 p-3 rounded-full mb-4" data-id="element-219">
            <Shield className="w-10 h-10 text-yellow-600" data-id="element-220" />
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3" data-id="element-221">
            🔒 Garantía sin riesgo
          </h3>
          <p className="text-base md:text-lg text-gray-600 mb-4 px-2" data-id="element-222">
            Probalo durante 7 días. Si no te da claridad, control y pedidos
            reales, te devolvemos el 100% del dinero.
          </p>
          <p className="font-bold text-gray-900" data-id="element-223">Sin vueltas. Sin preguntas.</p>
        </div>
      </section>

      {/* 12. TESTIMONIALS */}
      <section className="py-10 md:py-14 bg-gray-50" data-id="element-224">
        <div className="max-w-6xl mx-auto px-4" data-id="element-225">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-10" data-id="element-226">
            🗣️ Lo que dicen otros dueños gastronómicos
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-227">
            {TESTIMONIALS.map((t, i) => <div key={i} className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-228">
                <div className="flex text-yellow-400 mb-3" data-id="element-229">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-230" />)}
                </div>
                <p className="text-gray-700 italic mb-4 text-sm md:text-base" data-id="element-231">
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-3" data-id="element-232">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-500 text-sm" data-id="element-233">
                    {t.name.charAt(0)}
                  </div>
                  <div data-id="element-234">
                    <p className="font-bold text-gray-900 text-sm" data-id="element-235">{t.name}</p>
                    <p className="text-xs text-gray-500" data-id="element-236">
                      {t.age} – {t.city}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 13. FAQ */}
      <section className="py-10 md:py-14 bg-white" data-id="element-237">
        <div className="max-w-3xl mx-auto px-4" data-id="element-238">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8" data-id="element-239">
            ❓ Preguntas frecuentes
          </h3>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-240">
            {FAQS.map((faq, i) => <AccordionItem key={i} question={faq.q} answer={faq.a} data-id="element-241" />)}
          </div>
        </div>
      </section>

      {/* 14. FINAL CTA */}
      <section className="py-10 md:py-14 bg-gray-900 text-white text-center" data-id="element-242">
        <div className="max-w-4xl mx-auto px-4" data-id="element-243">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6 px-2" data-id="element-244">
            Tener un negocio gastronómico puede ser distinto
          </h2>
          <p className="text-base md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto px-2" data-id="element-245">
            No necesitás trabajar más. Necesitás un sistema que te sostenga
            todos los días.
          </p>

          <CtaButton className="animate-bounce-slow" data-id="element-246">
            👉 Accedé ahora a KIT DELIVERY PRO
          </CtaButton>
        </div>
      </section>

      {/* 15. SOCIAL PROOF POPUP */}
      <SocialProofPopup data-id="element-247" />

      {/* EXTRA PERSUASIVE BUTTON MID-PAGE (Floating or inserted) */}
      {/* We'll insert one more CTA section after bonuses as requested in strategic placement */}
    </div>;
}